import { Button, Card, Col, Descriptions, Flex, Space, Tag, Typography } from 'antd'
import type { DescriptionsItemType } from 'antd/es/descriptions'
import dayjs from 'dayjs'
import { useState } from 'react'

import { DownCircleFilled, UpCircleFilled } from '@ant-design/icons'
import { SubscriptionStatus } from '@components/SubscriptionStatus'
import { SeatIcon, PencilSimpleIcon } from '@phosphor-icons/react'

import type { Product, School, Subscription } from '../../../../types'

interface SubscriptionCardsProps {
    school: School
    refetch: () => void
    subscription: Subscription
    seatCount?: number
    usedSeats?: number
    invitedSeats?: number
    products: Product[]
    onEditSubscription: (subscription: Subscription) => void
    onEditSeats: (subscription: Subscription) => void
}

export const SubscriptionCard = ({
    subscription,
    seatCount,
    usedSeats,
    invitedSeats,
    onEditSubscription,
    onEditSeats
}: SubscriptionCardsProps) => {
    const [showMore, setShowMore] = useState<boolean>(false)

    const productKeys = subscription?.subscriptionProducts?.map(sp => sp.product.key).join(', ')
    const name = subscription?.name ? subscription.name : productKeys

    const subscriptionDetails: DescriptionsItemType[] = [
        {
            label: 'Status',
            children: <SubscriptionStatus status={subscription.status} />
        },
        {
            label: 'Products',
            children: (
                <Flex gap="small" wrap>
                    {subscription?.subscriptionProducts?.map(sp => (
                        <Tag key={sp.product.key}>{sp.product.key}</Tag>
                    ))}
                </Flex>
            )
        }
    ]

    if (showMore) {
        subscriptionDetails.push({
            label: 'ID',
            children: subscription.id
        })
        subscriptionDetails.push({
            label: 'Term',
            children: <Tag>{subscription.term}</Tag>
        })
        subscriptionDetails.push({
            label: 'Billable',
            children: subscription.isInternal ? 'No' : 'Yes'
        })
    }

    subscriptionDetails.push({
        label: 'Starts on',
        children: subscription?.startDate ? dayjs(subscription.startDate).format('MMM D, YYYY HH:mm:ss') : 'N/A'
    })
    subscriptionDetails.push({
        label: 'Ends on',
        children: subscription?.endDate ? dayjs(subscription.endDate).format('MMM D, YYYY HH:mm:ss') : 'N/A'
    })

    if (showMore) {
        if (subscription.stripeId) {
            subscriptionDetails.push({
                label: 'Stripe ID',
                children: (
                    <a
                        href={`https://dashboard.stripe.com/${import.meta.env.VITE_STRIPE_ACCOUNT}/test/subscriptions/${
                            subscription.stripeId
                        }`}
                        target="_blank"
                    >
                        {subscription.stripeId}
                    </a>
                )
            })
        }

        subscriptionDetails.push({
            label: 'Seat Count',
            children: seatCount
        })

        subscriptionDetails.push({
            label: 'Assigned Users',
            children: usedSeats
        })

        subscriptionDetails.push({
            label: 'Invited',
            children: invitedSeats
        })
    }

    const availableSeats = (seatCount ?? 0) - (usedSeats ?? 0) - (invitedSeats ?? 0)

    subscriptionDetails.push({
        label: 'Available Seats',
        children: (
            <Typography.Text>
                {availableSeats === 0 ? (
                    <Typography.Text style={{ color: 'red' }}>{availableSeats}</Typography.Text>
                ) : (
                    availableSeats
                )}
                {!showMore ? ` / ${seatCount}` : ''}
            </Typography.Text>
        )
    })

    const actions = [<PencilSimpleIcon key="edit" onClick={() => onEditSubscription(subscription)} />]
    if (subscription?.status !== 'CANCELLED') {
        actions.push(<SeatIcon key="seat" onClick={() => onEditSeats(subscription)} />)
    }

    return (
        <Col key={subscription.id}>
            <Card style={{ width: 400 }} title={name} actions={actions}>
                <Space vertical>
                    <Descriptions items={subscriptionDetails} column={1} />
                    <Button
                        type="link"
                        icon={showMore ? <UpCircleFilled /> : <DownCircleFilled />}
                        onClick={() => setShowMore(!showMore)}
                        style={{ fontSize: '0.8rem', padding: 0 }}
                    >
                        {showMore ? 'Show Less' : 'Show More'}
                    </Button>
                </Space>
            </Card>
        </Col>
    )
}
